import { useSearchParams } from 'react-router-dom';
import { signOut } from '../api/client.js';
import PublicShell from '../components/PublicShell.jsx';

/**
 * Terminal states from /auth/redirect. Each entry is
 * [headline, what to do about it, is retrying worth it].
 * Codes come from failToSpa() in api/src/auth/routes.js.
 */
const MESSAGES = {
  sign_in_expired: [
    'That sign-in attempt is no longer valid.',
    'It expired, was already used, or was started in a different browser session. Signing in again will fix it.',
    true
  ],
  not_authorised: [
    'Your account is not set up for this portal.',
    'You signed in with Microsoft successfully, but there is no user record for your account on this site. Ask an administrator to add you.',
    false
  ],
  account_disabled: [
    'Your account has been disabled.',
    'Contact your administrator to have it reactivated.',
    false
  ],
  wrong_tenant: [
    'Your account is in a different Microsoft tenant.',
    'This portal only accepts accounts from its own directory. Ask an administrator to invite you as a guest.',
    false
  ],
  /* The token exchange itself failed. Split into two codes because the advice
     is opposite: sign_in_config cannot be fixed by the person reading it. */
  sign_in_config: [
    'This portal cannot complete sign-in at the moment.',
    'Microsoft rejected the portal\'s own credentials, so nobody can sign in '
      + 'until it is fixed — it is not a problem with your account. The usual '
      + 'cause is an expired client secret on the app registration. Please '
      + 'report this to Vizora.',
    false
  ],
  sign_in_failed: [
    'Sign-in could not be completed.',
    'Microsoft did not return a usable token. This is often temporary, so try '
      + 'again — if it keeps happening, report it.',
    true
  ],
  account_conflict: [
    'This account is linked to a different Microsoft sign-in.',
    'Your portal account was already connected to another Microsoft account, '
      + 'so it cannot also be linked to this one. If you have two work '
      + 'accounts, sign in with the one you used first. Otherwise an '
      + 'administrator needs to check the account.',
    false
  ],
  nonce_mismatch: [
    'The sign-in response failed a security check.',
    'This can happen if the sign-in took too long. Try again — if it keeps happening, report it.',
    true
  ],
  missing_claims: [
    'Microsoft did not return the details we need.',
    'The token was missing an object id or email address. An administrator may need to check the token configuration on the app registration.',
    false
  ],
  access_denied: [
    'Sign-in was cancelled.',
    'You declined the sign-in or consent prompt.',
    true
  ],
  consent_required: [
    'This application needs administrator consent.',
    'An administrator must grant consent for the requested permissions before you can sign in.',
    false
  ],
  /* Raised by the client when a second sign-in bounce happens within seconds
     of the first. Almost always the account is authenticated by Entra but has
     no portal user record, so the server rejects it, the page retries, and
     Entra signs in silently each time. Offering "sign in again" here would
     just restart the loop. */
  account_inactive: [
    'Your account is switched off.',
    'You signed in successfully, but the account or the customer it belongs to '
      + 'is marked inactive, so the portal ended the session. An administrator '
      + 'needs to set IsActive on your user record and on the customer.',
    false
  ],
  no_user_context: [
    'Your account could not be loaded.',
    'You signed in successfully, but the portal found no record to load for '
      + 'your user id. This usually means the user row was deleted, or the '
      + 'database schema is not fully applied. An administrator should check '
      + 'portal.vUserContext.',
    false
  ],
  wrong_email_domain: [
    'Your email address is not accepted on this site.',
    'You signed in successfully, but your address is not in an email domain '
      + 'this customer allows. That usually means your account was set up '
      + 'against the wrong company, or you are on the wrong subdomain. Check '
      + 'the web address, then ask an administrator.',
    false
  ],
  signin_loop: [
    'Sign-in kept repeating, so we stopped it.',
    'Microsoft signed you in, but this portal rejected the account and the '
      + 'page kept retrying. The usual cause is that your email address has no '
      + 'user record for this site. Ask an administrator to add you, and to '
      + 'check the portal logs for "Sign-in rejected".',
    false
  ]
};

export default function SignInError() {
  const [params] = useSearchParams();

  /* There is no portal session on this page - the server redirects here
     before creating one - but /auth/signout still returns the Entra logout
     URL, which is the part that matters when the wrong account is signed in.
     If it fails, fall back to the account picker rather than leaving the
     button dead, which is the fault being fixed here. */
  const leave = async () => {
    try {
      await signOut();
    } catch {
      window.location.href = '/auth/signin?prompt=select_account';
    }
  };

  const code = params.get('error') ?? 'unknown';
  const [headline, detail, retryable] = MESSAGES[code]
    ?? ['Something went wrong signing you in.', null, true];

  return (
    <PublicShell eyebrow="Sign-in" heading={headline} lead={detail}>
      <div className="public-actions">
        {retryable && (
          <a className="btn btn-primary" href="/auth/signin">Sign in again</a>
        )}

        {/* The commonest cause of a refusal is being signed in to the
            wrong Entra account. Silent SSO then re-presents that same
            account on every attempt, so "sign in again" cannot escape it
            and the old "Back to start" link went to /, which bounced
            straight back here. prompt=select_account is the way out. */}
        <a className={retryable ? 'btn btn-outline-secondary' : 'btn btn-primary'}
           href="/auth/signin?prompt=select_account">
          Use a different account
        </a>

        {/* Full sign-out, for a shared machine or when the account picker
            itself is remembering something unwanted. */}
        <button type="button" className="btn btn-outline-secondary" onClick={leave}>
          Sign out of Microsoft
        </button>
      </div>

      {/* Kept, and kept quiet. Nobody reading this page wants a code, but the
          person they forward it to needs one - several of these messages end
          in "report this to Vizora". */}
      {code !== 'unknown' && (
        <p className="public-ref">Reference: <code>{code}</code></p>
      )}
    </PublicShell>
  );
}
